import { string, number, boolean, array, date, object, ref, mixed } from 'yup';
import moment from 'moment';
import * as errMsgs from './err-msgs';
import { sessionRemoteOrFace2FaceTypes } from '../constants/sessionData';
import { coursesOptions } from '../constants';
import './custom-functions';

const URLregex = /^((https?):\/\/)?(www.)?[a-z0-9]+(\.[a-z]{2,}){1,3}(#?\/?[a-zA-Z0-9#.-]+)*\/?(\?[a-zA-Z0-9-_.-]+=[a-zA-Z0-9-%?&=.-]+&?)?$/;

export const requiredText = string()
  .typeError(errMsgs.DEFAULT_REQUIRED)
  .required(errMsgs.DEFAULT_REQUIRED);

export const requiredDate = date()
  .typeError(errMsgs.DEFAULT_REQUIRED)
  .required(errMsgs.DEFAULT_REQUIRED);

export const requiredStartDate = date()
  .typeError(errMsgs.DEFAULT_REQUIRED)
  .required(errMsgs.DEFAULT_REQUIRED);

export const startTime = mixed().when(['endTime', 'clonedStartTime'], {
  is: (endTime, clonedStartTime) => {
    if (endTime && !clonedStartTime) return true;
    return (
      endTime &&
      moment(endTime, 'hh:mm').isBefore(moment(clonedStartTime, 'hh:mm'))
    );
  },
  then: schema => schema.oneOf([false], errMsgs.END_DATE_IS_BEFORE_START_DATE),
  otherwise: schema => schema.nullable().typeError(false)
});

export const optionalText = string()
  .typeError(errMsgs.DEFAULT_REQUIRED)
  .nullable();

export const dates = array()
  .of(
    object().shape({
      sessionDate: requiredDate,
      startTime: startTime,
      endTime: optionalText
    })
  )
  .min(1, 'errMsgs.MIN_1_DATE')
  .max(4, 'errMsgs.MAX_4_DATES')
  .required(errMsgs.DEFAULT_REQUIRED);

export const requiredPositiveNumber = number()
  .typeError(errMsgs.DEFAULT_REQUIRED)
  .min(1, errMsgs.NEGATIVE_NUMBER)
  .required(errMsgs.DEFAULT_REQUIRED);

export const email = string()
  .email(errMsgs.INVALID_EMAIL)
  .max(100, errMsgs.TOO_LONG_MAX_100)
  .required(errMsgs.DEFAULT_REQUIRED)
  .typeError(errMsgs.DEFAULT_REQUIRED);

export const name = string()
  .max(100, errMsgs.TOO_LONG_MAX_100)
  .required(errMsgs.DEFAULT_REQUIRED)
  .typeError(errMsgs.DEFAULT_REQUIRED);

export const password = string()
  .matches(
    /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[§±!@£$%^&*()+={#}:;'"|,.?\/\\[\]<>_-])[A-Za-z\d§±!@£$%^&*()-+={#}:;'"|,.?\/\\[\]<>_-]{8,}$/gm,
    errMsgs.SHORT_PASSWORD
  )
  .required(errMsgs.DEFAULT_REQUIRED);

export const passwordConfirmation = string()
  .oneOf([ref('password'), null], errMsgs.PASSWORDS_MUST_MATCH)
  .required(errMsgs.DEFAULT_REQUIRED);

export const loginPassword = string().required(errMsgs.DEFAULT_REQUIRED);

export const postcode = string()
  .required(errMsgs.DEFAULT_REQUIRED)
  .min(5, errMsgs.TOO_SHORT_MIN_5)
  .max(8, errMsgs.TOO_LONG_MAX_7)
  .matches(
    /\b(([a-z][0-9]{1,2})|(([a-z][a-hj-y][0-9]{1,2})|(([a-z][0-9][a-z])|([a-z][a-hj-y][0-9]?[a-z]))))( |)[0-9][a-z]{2}\b/gi,
    errMsgs.INVALID_POSTCODE
  );

export const agreedOnTerms = boolean()
  .oneOf([true], errMsgs.SHOULD_AGREE_ON_TERMS)
  .required(errMsgs.DEFAULT_REQUIRED);

export const confirmAttendance = boolean()
  .oneOf([true], errMsgs.SHOULD_CONFIRM_YOUNG_PEOPLE_ATTENDANCE)
  .required(errMsgs.DEFAULT_REQUIRED);

export const optionalDate = date()
  .typeError(errMsgs.DEFAULT_REQUIRED)
  .nullable();

export const urlRequired = string()
  .matches(URLregex, {
    message: errMsgs.INVALID_LINK
  })
  .required(errMsgs.DEFAULT_REQUIRED);

export const link = string().matches(URLregex, {
  message: errMsgs.INVALID_LINK
});

export const optionalPhoneNumber = string().when((value, schema) => {
  if (value) {
    return schema
      .phone()
      .min(9, errMsgs.INVALID_PHONE)
      .max(12, errMsgs.INVALID_PHONE)
      .typeError(errMsgs.INVALID_PHONE);
  }
  return schema.nullable();
});

export const phoneNumber = string()
  .required(errMsgs.DEFAULT_REQUIRED)
  .min(9, errMsgs.INVALID_PHONE)
  .max(12, errMsgs.INVALID_PHONE)
  .when((value, schema) => {
    return schema.phone().typeError(errMsgs.INVALID_PHONE);
  });

export const optionalCheckbox = boolean()
  .typeError(errMsgs.DEFAULT_REQUIRED)
  .nullable();

export const numberField = number()
  .typeError(errMsgs.DEFAULT_REQUIRED)
  .required(errMsgs.DEFAULT_REQUIRED);

export const arrayOfString = array()
  .of(string())
  .min(1, errMsgs.EMPTY_ARRAY)
  .required(errMsgs.DEFAULT_REQUIRED)
  .typeError(errMsgs.DEFAULT_REQUIRED);

export const optionalArrayOfString = array()
  .of(string())
  .nullable();

export const sessionModules = array()
  .of(string())
  .min(1, errMsgs.EMPTY_ARRAY)
  .max(1, 'A session can only have one module')
  .required(errMsgs.DEFAULT_REQUIRED)
  .typeError(errMsgs.DEFAULT_REQUIRED);

export const organisationOther = string().when('showOrgOther', {
  is: true,
  then: schema =>
    schema
      .typeError(errMsgs.DEFAULT_REQUIRED)
      .required(errMsgs.DEFAULT_REQUIRED),
  otherwise: schema => schema.typeError(errMsgs.DEFAULT_REQUIRED).nullable()
});

const allowedSessionValues = sessionRemoteOrFace2FaceTypes.map(
  session => session.value
);
export const sessionType = string()
  .oneOf(allowedSessionValues, errMsgs.VALID_SESSION_TYPE)
  .required(errMsgs.DEFAULT_REQUIRED)
  .typeError(errMsgs.DEFAULT_REQUIRED);

const allowedCourses = coursesOptions.map(session => session.value);
export const coursesTypes = string()
  // .oneOf(allowedCourses, errMsgs.VALID_COURSE)
  .required(errMsgs.DEFAULT_REQUIRED)
  .typeError(errMsgs.DEFAULT_REQUIRED);

export const partnerTrainer = object().shape({
  key: string().required(errMsgs.DEFAULT_REQUIRED),
  label: string().required(errMsgs.DEFAULT_REQUIRED)
});

export const address = object().when('sessionType', {
  is: 'faceToFace',
  then: schema =>
    schema.shape({
      addressLine1: string().required(errMsgs.DEFAULT_REQUIRED),
      addressLine2: string()
        .nullable()
        .typeError(errMsgs.DEFAULT_REQUIRED),
      town: string().required(errMsgs.DEFAULT_REQUIRED),
      postcode: postcode
    }),
  otherwise: schema => schema.nullable()
});

export const onlineDetails = object().when('sessionType', {
  is: 'online',
  then: schema =>
    schema.shape({
      platform: string().required(errMsgs.DEFAULT_REQUIRED),
      link: string()
        .when(['platform'], {
          is: platform => platform === 'zoom',
          then: schema =>
            schema.matches(/((https?):\/\/)?(zoom)+.+/, errMsgs.ZOOM_LINK)
        })
        .when(['platform'], {
          is: platform => platform === 'teams',
          then: schema =>
            schema.matches(
              /((https?):\/\/)?(teams.microsoft)+.+/,
              errMsgs.TEAMS_LINK
            )
        })
        .when(['platform'], {
          is: platform => platform === 'google',
          then: schema =>
            schema.matches(
              /((https?):\/\/)?(meet.google)+.+/,
              errMsgs.MEET_LINK
            )
        })
        .required(errMsgs.DEFAULT_REQUIRED)
    }),
  otherwise: schema => schema.nullable()
});
